import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  CalendarCheck, Plus, Edit, Trash2, Save, X, Download, FileText,
  Clock, CheckCircle, Circle, Loader2, Users, AlertCircle, Filter
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import AuthorizationModal from "../components/AuthorizationModal";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api/agenda-trabajo";
const TABS = { AGENDA: "agenda", RESPONSABLES: "responsables" };
const ESTADOS = [
  { value: "no_iniciado", label: "No Iniciado", color: "bg-gray-100 text-gray-700", dot: "bg-gray-400" },
  { value: "iniciado", label: "Iniciado", color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-400" },
  { value: "en_proceso", label: "En Proceso", color: "bg-blue-100 text-blue-700", dot: "bg-blue-400" },
  { value: "concluido", label: "Concluido", color: "bg-green-100 text-green-700", dot: "bg-green-400" }
];
const PRIORIDADES = [
  { value: "baja", label: "Baja", color: "text-gray-500" },
  { value: "media", label: "Media", color: "text-amber-600" },
  { value: "alta", label: "Alta", color: "text-red-600" }
];

export default function AgendaTrabajoPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(TABS.AGENDA);

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar user={user} />
      <div className="flex-1 p-4 md:p-6 ml-0 md:ml-64">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <CalendarCheck className="w-7 h-7 text-indigo-600" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agenda de Trabajo</h1>
          </div>
          <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
            {[
              { id: TABS.AGENDA, label: "Actividades", icon: CalendarCheck },
              { id: TABS.RESPONSABLES, label: "Responsables", icon: Users }
            ].map(tab => (
              <button key={tab.id} data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${activeTab === tab.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                onClick={() => setActiveTab(tab.id)}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
          {activeTab === TABS.AGENDA && <ActividadesTab />}
          {activeTab === TABS.RESPONSABLES && <ResponsablesTab />}
        </div>
      </div>
    </div>
  );
}

// ============ ACTIVIDADES TAB ============
function ActividadesTab() {
  const { getAuthHeaders } = useAuth();
  const [actividades, setActividades] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editAct, setEditAct] = useState(null);
  const [filterEstado, setFilterEstado] = useState("");
  const [filterResp, setFilterResp] = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [exporting, setExporting] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const [actRes, respRes] = await Promise.all([
        axios.get(`${API}/actividades`, getAuthHeaders()),
        axios.get(`${API}/responsables`, getAuthHeaders())
      ]);
      setActividades(actRes.data);
      setResponsables(respRes.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeaders]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = (id) => {
    if (!window.confirm("¿Eliminar esta actividad? Se requerirá autorización.")) return;
    setPendingDeleteId(id);
    setShowAuthModal(true);
  };

  const executeDeleteWithAuth = async (authToken, authorizerName) => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`${API}/actividades/${pendingDeleteId}?authorization_token=${authToken}`, getAuthHeaders());
      toast.success(`Actividad eliminada. Autorizado por: ${authorizerName}`);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
    finally { setPendingDeleteId(null); }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const qp = new URLSearchParams();
      if (filterEstado) qp.set("estado", filterEstado);
      if (filterFechaDesde) qp.set("fecha_desde", filterFechaDesde);
      if (filterFechaHasta) qp.set("fecha_hasta", filterFechaHasta);
      if (filterResp) qp.set("responsable_id", filterResp);
      const qs = qp.toString() ? `?${qp.toString()}` : "";
      const res = await axios.get(`${API}/exportar/${format}${qs}`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agenda_trabajo.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Error al exportar"); }
    setExporting("");
  };

  const filtered = actividades.filter(a => {
    if (filterEstado && a.estado !== filterEstado) return false;
    if (filterResp && a.responsable_id !== filterResp) return false;
    if (filterFechaDesde && a.fecha_inicio && a.fecha_inicio < filterFechaDesde) return false;
    if (filterFechaHasta && a.fecha_fin && a.fecha_fin > filterFechaHasta) return false;
    if (filterFechaDesde && !a.fecha_inicio) return false;
    if (filterFechaHasta && !a.fecha_fin) return false;
    return true;
  });

  const hasActiveFilters = filterEstado || filterResp || filterFechaDesde || filterFechaHasta;
  const clearAllFilters = () => { setFilterEstado(""); setFilterResp(""); setFilterFechaDesde(""); setFilterFechaHasta(""); };

  // Stats
  const stats = { total: actividades.length, no_iniciado: 0, iniciado: 0, en_proceso: 0, concluido: 0 };
  actividades.forEach(a => { stats[a.estado] = (stats[a.estado] || 0) + 1; });

  const [chartSelectedEstado, setChartSelectedEstado] = useState(null);
  const CHART_SEGMENTS = [
    { key: "no_iniciado", name: "No Iniciado", color: "#9ca3af", dot: "bg-gray-400" },
    { key: "iniciado", name: "Iniciado", color: "#facc15", dot: "bg-yellow-400" },
    { key: "en_proceso", name: "En Proceso", color: "#60a5fa", dot: "bg-blue-400" },
    { key: "concluido", name: "Concluido", color: "#4ade80", dot: "bg-green-400" }
  ];
  const chartActividades = chartSelectedEstado ? actividades.filter(a => a.estado === chartSelectedEstado) : [];

  return (
    <div className="space-y-4" data-testid="actividades-tab">
      {/* Stats - clickable filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, filter: "", color: "bg-indigo-50 text-indigo-700 border-indigo-200", activeRing: "ring-2 ring-indigo-500" },
          { label: "No Iniciado", value: stats.no_iniciado, filter: "no_iniciado", color: "bg-gray-50 text-gray-700 border-gray-200", activeRing: "ring-2 ring-gray-500" },
          { label: "Iniciado", value: stats.iniciado, filter: "iniciado", color: "bg-yellow-50 text-yellow-700 border-yellow-200", activeRing: "ring-2 ring-yellow-500" },
          { label: "En Proceso", value: stats.en_proceso, filter: "en_proceso", color: "bg-blue-50 text-blue-700 border-blue-200", activeRing: "ring-2 ring-blue-500" },
          { label: "Concluido", value: stats.concluido, filter: "concluido", color: "bg-green-50 text-green-700 border-green-200", activeRing: "ring-2 ring-green-500" }
        ].map(s => {
          const isActive = filterEstado === s.filter;
          return (
            <div key={s.label} data-testid={`stat-card-${s.filter || "total"}`}
              className={`border rounded-lg px-4 py-3 cursor-pointer transition-all hover:shadow-md ${s.color} ${isActive ? `${s.activeRing} shadow-md scale-[1.02]` : "opacity-80 hover:opacity-100"}`}
              onClick={() => setFilterEstado(isActive ? "" : s.filter)}>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Donut Chart - distribution by status with activity list */}
      {stats.total > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-testid="chart-distribucion">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Distribución por Estado</h3>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Chart */}
            <div className="h-52 w-full md:w-1/2 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={CHART_SEGMENTS.filter(s => stats[s.key] > 0).map(s => ({ name: s.name, value: stats[s.key], key: s.key }))}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                    paddingAngle={3} dataKey="value" stroke="none"
                    cursor="pointer"
                    onClick={(data) => setChartSelectedEstado(prev => prev === data.key ? null : data.key)}
                  >
                    {CHART_SEGMENTS.filter(s => stats[s.key] > 0).map((s, i) => (
                      <Cell key={i} fill={s.color} opacity={chartSelectedEstado && chartSelectedEstado !== s.key ? 0.35 : 1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} actividad(es)`, name]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "12px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Activity list for selected segment */}
            <div className="w-full md:w-1/2 min-h-[100px]">
              {chartSelectedEstado ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${CHART_SEGMENTS.find(s => s.key === chartSelectedEstado)?.dot}`} />
                      {CHART_SEGMENTS.find(s => s.key === chartSelectedEstado)?.name} ({chartActividades.length})
                    </span>
                    <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setChartSelectedEstado(null)} data-testid="chart-clear-selection">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="chart-activity-list">
                    {chartActividades.map(a => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-md text-sm">
                        <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{a.nombre}</span>
                        {a.responsable_nombre && <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{a.responsable_nombre}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  <p>Haga clic en un segmento del gráfico para ver las actividades</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-gray-400" />
          <select data-testid="filter-estado" className="text-sm border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <select data-testid="filter-responsable" className="text-sm border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={filterResp} onChange={e => setFilterResp(e.target.value)}>
            <option value="">Todos los responsables</option>
            {responsables.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">Desde:</span>
            <input type="date" data-testid="filter-fecha-desde" className="text-sm border rounded-lg px-2 py-2 dark:bg-gray-700 dark:border-gray-600" value={filterFechaDesde} onChange={e => setFilterFechaDesde(e.target.value)} />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">Hasta:</span>
            <input type="date" data-testid="filter-fecha-hasta" className="text-sm border rounded-lg px-2 py-2 dark:bg-gray-700 dark:border-gray-600" value={filterFechaHasta} onChange={e => setFilterFechaHasta(e.target.value)} />
          </div>
          {hasActiveFilters && (
            <button data-testid="btn-clear-filters" className="text-xs text-red-500 hover:text-red-700 underline ml-1" onClick={clearAllFilters}>
              Limpiar filtros
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport("excel")} disabled={!!exporting} data-testid="btn-export-excel">
            {exporting === "excel" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} disabled={!!exporting} data-testid="btn-export-pdf">
            {exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}PDF
          </Button>
          <Button onClick={() => { setEditAct(null); setShowForm(true); }} data-testid="btn-nueva-actividad">
            <Plus className="w-4 h-4 mr-1" />Nueva Actividad
          </Button>
        </div>
      </div>

      {/* Form */}
      {showForm && <ActividadForm actividad={editAct} responsables={responsables} onSave={() => { setShowForm(false); fetch(); }} onCancel={() => setShowForm(false)} />}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <CalendarCheck className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No hay actividades registradas</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabla-actividades">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Actividad</th>
                  <th className="text-left px-4 py-3 font-medium">Responsable</th>
                  <th className="text-left px-4 py-3 font-medium">Inicio</th>
                  <th className="text-left px-4 py-3 font-medium">Fin</th>
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                  <th className="text-left px-4 py-3 font-medium">Prioridad</th>
                  <th className="text-left px-4 py-3 font-medium">Conclusión</th>
                  <th className="text-left px-4 py-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const est = ESTADOS.find(e => e.value === a.estado) || ESTADOS[0];
                  const pri = PRIORIDADES.find(p => p.value === a.prioridad);
                  const isOverdue = a.fecha_fin && a.estado !== "concluido" && new Date(a.fecha_fin) < new Date();
                  return (
                    <tr key={a.id} className={`border-t border-gray-100 dark:border-gray-700 ${isOverdue ? "bg-red-50/50 dark:bg-red-900/10" : ""}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{a.nombre}</p>
                          {a.observaciones && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{a.observaciones}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{a.responsable_nombre || "-"}</td>
                      <td className="px-4 py-3 text-gray-600">{a.fecha_inicio || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={isOverdue ? "text-red-600 font-medium" : "text-gray-600"}>
                          {a.fecha_fin || "-"}
                          {isOverdue && <AlertCircle className="w-3.5 h-3.5 inline ml-1" />}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${est.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${est.dot}`} />{est.label}
                        </span>
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium ${pri?.color || ""}`}>{pri?.label || a.prioridad}</span></td>
                      <td className="px-4 py-3 text-gray-600">{a.fecha_conclusion || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button className="p-1 text-gray-400 hover:text-blue-500" onClick={() => { setEditAct(a); setShowForm(true); }} data-testid={`btn-edit-act-${a.id}`}><Edit className="w-4 h-4" /></button>
                          <button className="p-1 text-gray-400 hover:text-red-500" onClick={() => handleDelete(a.id)} data-testid={`btn-delete-act-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Authorization Modal for deletion */}
      <AuthorizationModal
        isOpen={showAuthModal}
        onClose={() => { setShowAuthModal(false); setPendingDeleteId(null); }}
        onAuthorized={executeDeleteWithAuth}
        title="Autorización para Eliminar Actividad"
        description="Para eliminar esta actividad de la agenda de trabajo, ingrese las credenciales de un usuario autorizado."
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}

// ============ ACTIVIDAD FORM ============
function ActividadForm({ actividad, responsables, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({
    nombre: actividad?.nombre || "",
    descripcion: actividad?.descripcion || "",
    responsable_id: actividad?.responsable_id || "",
    fecha_inicio: actividad?.fecha_inicio || "",
    fecha_fin: actividad?.fecha_fin || "",
    estado: actividad?.estado || "no_iniciado",
    fecha_conclusion: actividad?.fecha_conclusion || "",
    observaciones: actividad?.observaciones || "",
    prioridad: actividad?.prioridad || "media"
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (actividad?.id) {
        await axios.put(`${API}/actividades/${actividad.id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/actividades`, form, getAuthHeaders());
      }
      onSave();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="actividad-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
      <h3 className="font-semibold text-lg">{actividad ? "Editar Actividad" : "Nueva Actividad"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Nombre de la Actividad *</label>
          <input data-testid="input-nombre-actividad" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} required />
        </div>
        <div><label className="block text-sm font-medium mb-1">Responsable</label>
          <select data-testid="select-responsable" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.responsable_id} onChange={e => setForm({...form, responsable_id: e.target.value})}>
            <option value="">Sin asignar</option>
            {responsables.map(r => <option key={r.id} value={r.id}>{r.nombre} {r.cargo ? `(${r.cargo})` : ""}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><label className="block text-sm font-medium mb-1">Fecha Inicio</label>
          <input type="date" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.fecha_inicio} onChange={e => setForm({...form, fecha_inicio: e.target.value})} />
        </div>
        <div><label className="block text-sm font-medium mb-1">Fecha Fin</label>
          <input type="date" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.fecha_fin} onChange={e => setForm({...form, fecha_fin: e.target.value})} />
        </div>
        <div><label className="block text-sm font-medium mb-1">Estado</label>
          <select data-testid="select-estado" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.estado} onChange={e => {
            const newState = e.target.value;
            const updates = { estado: newState };
            if (newState === "concluido" && !form.fecha_conclusion) {
              updates.fecha_conclusion = new Date().toISOString().split("T")[0];
            }
            if (newState !== "concluido") updates.fecha_conclusion = "";
            setForm(prev => ({...prev, ...updates}));
          }}>
            {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
        </div>
        <div><label className="block text-sm font-medium mb-1">Prioridad</label>
          <select className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.prioridad} onChange={e => setForm({...form, prioridad: e.target.value})}>
            {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>
      {form.estado === "concluido" && (
        <div className="max-w-xs">
          <label className="block text-sm font-medium mb-1">Fecha de Conclusión</label>
          <input type="date" data-testid="input-fecha-conclusion" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.fecha_conclusion} onChange={e => setForm({...form, fecha_conclusion: e.target.value})} />
        </div>
      )}
      <div><label className="block text-sm font-medium mb-1">Observaciones</label>
        <textarea className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" rows={2} value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} placeholder="Notas adicionales..." />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" data-testid="btn-guardar-actividad" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar
        </Button>
      </div>
    </form>
  );
}

// ============ RESPONSABLES TAB ============
function ResponsablesTab() {
  const { getAuthHeaders } = useAuth();
  const [responsables, setResponsables] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editResp, setEditResp] = useState(null);

  const fetch = useCallback(async () => {
    try { const res = await axios.get(`${API}/responsables`, getAuthHeaders()); setResponsables(res.data); } catch (e) { console.error(e); }
  }, [getAuthHeaders]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este responsable?")) return;
    try { await axios.delete(`${API}/responsables/${id}`, getAuthHeaders()); fetch(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="responsables-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Gestione los responsables que pueden ser asignados a actividades de la agenda.</p>
        <Button onClick={() => { setEditResp(null); setShowForm(true); }} data-testid="btn-nuevo-responsable">
          <Plus className="w-4 h-4 mr-1" />Nuevo Responsable
        </Button>
      </div>

      {showForm && <ResponsableForm responsable={editResp} onSave={() => { setShowForm(false); fetch(); }} onCancel={() => setShowForm(false)} />}

      {responsables.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No hay responsables registrados</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {responsables.map(r => (
            <div key={r.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 font-bold text-sm">
                {r.nombre.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{r.nombre}</p>
                {r.cargo && <p className="text-xs text-gray-500">{r.cargo}</p>}
                {r.departamento && <p className="text-xs text-gray-400">{r.departamento}</p>}
                {r.email && <p className="text-xs text-indigo-500">{r.email}</p>}
              </div>
              <div className="flex gap-1">
                <button className="p-1 text-gray-400 hover:text-blue-500" onClick={() => { setEditResp(r); setShowForm(true); }} data-testid={`btn-edit-resp-${r.id}`}><Edit className="w-4 h-4" /></button>
                <button className="p-1 text-gray-400 hover:text-red-500" onClick={() => handleDelete(r.id)} data-testid={`btn-delete-resp-${r.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ RESPONSABLE FORM ============
function ResponsableForm({ responsable, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({
    nombre: responsable?.nombre || "",
    cargo: responsable?.cargo || "",
    departamento: responsable?.departamento || "",
    email: responsable?.email || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (responsable?.id) {
        await axios.put(`${API}/responsables/${responsable.id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/responsables`, form, getAuthHeaders());
      }
      onSave();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="responsable-form" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
      <h3 className="font-semibold text-lg">{responsable ? "Editar Responsable" : "Nuevo Responsable"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Nombre *</label>
          <input data-testid="input-nombre-responsable" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} required />
        </div>
        <div><label className="block text-sm font-medium mb-1">Cargo</label>
          <input className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.cargo} onChange={e => setForm({...form, cargo: e.target.value})} />
        </div>
        <div><label className="block text-sm font-medium mb-1">Departamento</label>
          <input className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.departamento} onChange={e => setForm({...form, departamento: e.target.value})} />
        </div>
        <div><label className="block text-sm font-medium mb-1">Email</label>
          <input type="email" className="w-full border rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" data-testid="btn-guardar-responsable" disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar
        </Button>
      </div>
    </form>
  );
}
